import React, { useState } from 'react';
import { Grid, Typography, Chip, Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteConfirmationDialog from '../../../components/DeleteConfirmationDialog';
import FoodAnalysisModal from './FoodAnalysisModal';
import FoodAnalysisActions from './FoodAnalysisActions';

const FoodAnalysisCard = ({ analysis, onDelete, onDownload, onViewDetails, analysisDetails }) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [modalAnalysisData, setModalAnalysisData] = useState(null);

  const getStatusTranslation = (status) => {
    return t(`foodAnalysisObj.${status}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#4CAF50';
      case 'failed':
        return '#f44336';
      default:
        return '#FF9800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    setIsDeleting(true);
    try {
      await onDelete(analysis._id);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = async () => {
    if (!onDownload) return;

    setIsDownloading(true);
    try {
      await onDownload(analysis);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleViewDetails = () => {
    onViewDetails(analysis);
    setModalAnalysisData(analysisDetails);
    setShowDetailsModal(true);
  };

  const canDownload = analysis.status === 'completed' && analysis.nutritionEstimate;
  const canDelete = analysis.status === 'failed' || analysis.status === 'completed';

  return (
    <>
      <Box
        style={{
          marginBottom: '20px',
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #e0e0e0',
          background: '#fff',
        }}
      >
        <Grid container spacing={2}>
          {/* Image Section */}
          <Grid item xs={12} md={4}>
            <Box
              style={{
                width: '100%',
                height: '200px',
                borderRadius: '10px',
                overflow: 'hidden',
                background: '#f5f5f7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={analysis.imageUrl}
                alt={analysis.mealName || 'Food Image'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <Box
                style={{
                  display: 'none',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666',
                }}
              >
                <Typography variant="body2">{t('common.imageNotAvailable')}</Typography>
              </Box>
            </Box>
          </Grid>

          {/* Content Section */}
          <Grid item xs={12} md={8}>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '10px',
              }}
            >
              <Box>
                <Typography variant="h6" style={{ fontWeight: '600', color: '#333' }}>
                  {analysis.mealName || t('foodAnalysisObj.unnamedMeal')}
                </Typography>
                <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <Typography variant="subtitle2" style={{ fontWeight: '600', color: '#333', marginRight: '8px' }}>
                    {t('foodAnalysisObj.status')}:
                  </Typography>
                  <Box
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: getStatusColor(analysis.status),
                      marginRight: '6px',
                    }}
                  />
                  <span
                    style={{
                      color: getStatusColor(analysis.status),
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {getStatusTranslation(analysis.status)}
                  </span>
                </Box>
              </Box>
              <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FoodAnalysisActions
                  canDownload={canDownload}
                  canDelete={canDelete}
                  isDownloading={isDownloading}
                  isDeleting={isDeleting}
                  onViewDetails={handleViewDetails}
                  onDownload={handleDownload}
                  onDelete={() => setShowDeleteDialog(true)}
                />
              </Box>
            </Box>

            <Typography variant="body2" style={{ color: '#666', marginBottom: '15px' }}>
              {formatDate(analysis.uploadDate)}
            </Typography>

            {analysis.status === 'pending' && (
              <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                <CircularProgress size={16} style={{ color: '#26786a' }} />
                <Typography variant="body2" style={{ color: '#FF9800' }}>
                  {t('foodAnalysisObj.analysisInProgress')}
                </Typography>
              </Box>
            )}

            {analysis.status === 'completed' && analysis.nutritionEstimate && (
              <Box style={{ marginBottom: '15px' }}>
                <Typography variant="subtitle2" style={{ fontWeight: '600', marginBottom: '8px', color: '#333' }}>
                  {t('foodAnalysisObj.nutritionEstimate')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box style={{ textAlign: 'center', padding: '8px', background: '#f8f9fa', borderRadius: '8px' }}>
                      <Typography variant="h6" style={{ color: '#26786A', fontWeight: '600' }}>
                        {analysis.nutritionEstimate.calories || 0}
                      </Typography>
                      <Typography variant="caption" style={{ color: '#666' }}>
                        {t('foodAnalysisObj.calories')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box style={{ textAlign: 'center', padding: '8px', background: '#f8f9fa', borderRadius: '8px' }}>
                      <Typography variant="h6" style={{ color: '#26786A', fontWeight: '600' }}>
                        {analysis.nutritionEstimate.protein_g || 0}g
                      </Typography>
                      <Typography variant="caption" style={{ color: '#666' }}>
                        {t('foodAnalysisObj.protein')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box style={{ textAlign: 'center', padding: '8px', background: '#f8f9fa', borderRadius: '8px' }}>
                      <Typography variant="h6" style={{ color: '#26786A', fontWeight: '600' }}>
                        {analysis.nutritionEstimate.carbohydrates_g || 0}g
                      </Typography>
                      <Typography variant="caption" style={{ color: '#666' }}>
                        {t('foodAnalysisObj.carbs')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box style={{ textAlign: 'center', padding: '8px', background: '#f8f9fa', borderRadius: '8px' }}>
                      <Typography variant="h6" style={{ color: '#26786A', fontWeight: '600' }}>
                        {analysis.nutritionEstimate.fat_g || 0}g
                      </Typography>
                      <Typography variant="caption" style={{ color: '#666' }}>
                        {t('foodAnalysisObj.fat')}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {analysis.status === 'completed' && analysis.recommendation?.alternative_suggestions?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" style={{ fontWeight: '600', marginBottom: '8px', color: '#333' }}>
                  {t('foodAnalysisObj.recommendations')}
                </Typography>
                <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {analysis.recommendation.alternative_suggestions.map((suggestion, index) => (
                    <Chip
                      key={index}
                      label={suggestion}
                      size="small"
                      style={{
                        backgroundColor: '#e8f5e8',
                        color: '#26786A',
                        border: '1px solid #26786A',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {analysis.status === 'failed' && analysis.errorMessage && (
              <Box style={{ marginTop: '10px' }}>
                <Typography variant="body2" style={{ color: '#f44336', fontStyle: 'italic' }}>
                  {t('foodAnalysisObj.error')}: {analysis.errorMessage}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      <DeleteConfirmationDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('foodAnalysisObj.deleteDialog.title')}
        message={t('foodAnalysisObj.deleteDialog.message')}
        isLoading={isDeleting}
        loadingText={t('foodAnalysisObj.deleteDialog.loading')}
      />

      <FoodAnalysisModal
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        analysisData={modalAnalysisData}
        isLoading={false}
      />
    </>
  );
};

export default FoodAnalysisCard;
